import { useQuery } from '@tanstack/react-query';
import Lottie from 'react-lottie';

import utilizationGraphLottie from '~/shared/assets/lotties/datafy-graph.json';
import { GraphResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Card from '~/shared/components/Card';
import Typography from '~/shared/components/Typography';

import LineChart from '../DashboardCharts/LineChart';
import ChartFilterDropdown from '../DashboardCharts/ChartFilterDropdown';

import { dateFilters } from './helpers';

export default function UtlizationChart({
  activeFilter,
  queryString,
  onSelect,
}: {
  activeFilter: (typeof dateFilters)[number];
  queryString?: string;
  onSelect: (selection: (typeof dateFilters)[number]) => void;
}) {
  return (
    <Card className="flex flex-col gap-[20px]">
      <div className="h-[48px] flex justify-between items-center">
        <Typography variant="H5">Utilization</Typography>
        <ChartFilterDropdown
          items={dateFilters.map((item) => ({
            title: item.title,
            fn: () => item,
          }))}
          onSelect={onSelect}
          selectedItemIndex={dateFilters.indexOf(activeFilter)}
        />
      </div>
      <div className="flex-1 relative">
        <UtilizationChart
          queryUrl={`/api/v1/volumes/utilization-graph?${queryString}`}
          activeFilter={activeFilter?.title}
        />
      </div>
    </Card>
  );
}

function EmptyState() {
  return (
    <Typography variant="p3" className="w-full h-full flex justify-center items-center">
      No Results
    </Typography>
  );
}

function UtilizationChart({ queryUrl, activeFilter }: { queryUrl: string; activeFilter: string }) {
  const api = useXFetch();
  const { data, isLoading, isRefetching, error } = useQuery({
    queryKey: [queryUrl],
    queryFn: () => api<GraphResponse>(queryUrl),
    refetchOnWindowFocus: false,
  });

  if (isLoading || isRefetching) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData: utilizationGraphLottie,
            rendererSettings: {
              preserveAspectRatio: 'none',
            },
          }}
          height={208.92}
          width={192}
        />
      </div>
    );
  }

  if (error || !data || !data?.utilizationGraph?.length) {
    return <EmptyState />;
  }

  return <LineChart activeFilter={activeFilter} data={data.utilizationGraph} />;
}
